import React, { useEffect, useRef } from "react";

import { isLand } from "../data/worldOutline";

/**
 * Where we operate, drawn rather than listed.
 *
 * A dot sphere generated procedurally — no map data and no third-party asset —
 * with a marker at each delivery region. It turns slowly so all three regions
 * come round; under prefers-reduced-motion it holds a fixed angle with the
 * Americas and India both in view.
 */

// labelDy staggers the captions so neighbouring markers do not collide.
export const OFFICES = [
  { region: "CANADA", city: "Canada", lat: 56.0, lon: -96.0, labelDy: -18 },
  { region: "USA", city: "United States", lat: 39.0, lon: -98.0, labelDy: 22 },
  { region: "INDIA", city: "India", lat: 21.0, lon: 78.0, labelDy: -18 },
];

const RAD = Math.PI / 180;

function GlobeOffices() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Latitude/longitude lattice, thinned towards the poles so the dots stay
    // evenly spaced on the surface. Dots that fall on land are drawn brightly
    // and the ocean is left as a much fainter wash, which reads as a world map
    // without needing any cartographic dataset.
    const dots = [];
    for (let lat = -84; lat <= 84; lat += 4) {
      const ring = Math.max(8, Math.round(90 * Math.cos(lat * RAD)));
      for (let i = 0; i < ring; i += 1) {
        const lon = -180 + (360 / ring) * i;
        dots.push({ lat, lon, land: isLand(lon, lat) });
      }
    }

    // 98° brings the Americas to the centre of the disc when the globe is held still.
    let spin = reduce ? 98 : 0;
    let frame = 0;
    let size = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      size = Math.min(rect.width, rect.height);
      if (!size) return;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const project = (lat, lon, radius, cx, cy) => {
      const a = (lon + spin) * RAD;
      const b = lat * RAD;
      return {
        x: cx + Math.cos(b) * Math.sin(a) * radius,
        y: cy - Math.sin(b) * radius,
        z: Math.cos(b) * Math.cos(a),
      };
    };

    const draw = () => {
      const rect = canvas.getBoundingClientRect();
      const cx = rect.width / 2;
      const cy = rect.height / 2;
      const radius = Math.min(rect.width, rect.height) / 2 - 20;

      ctx.clearRect(0, 0, rect.width, rect.height);
      if (radius <= 0) {
        if (!reduce) frame = window.requestAnimationFrame(draw);
        return;
      }

      // faint horizon
      ctx.beginPath();
      ctx.arc(cx, cy, radius + 10, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(6,64,220,0.16)";
      ctx.lineWidth = 1;
      ctx.stroke();

      dots.forEach((d) => {
        const p = project(d.lat, d.lon, radius, cx, cy);
        if (p.z <= 0) return;
        ctx.beginPath();
        ctx.arc(p.x, p.y, d.land ? 1.5 : 0.9, 0, Math.PI * 2);
        ctx.fillStyle = d.land
          ? `rgba(6,64,220,${0.35 + p.z * 0.55})`
          : `rgba(6,64,220,${0.05 + p.z * 0.07})`;
        ctx.fill();
      });

      OFFICES.forEach((o) => {
        const p = project(o.lat, o.lon, radius, cx, cy);
        if (p.z <= 0) return;
        const alpha = Math.min(1, 0.25 + p.z);

        const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 16);
        glow.addColorStop(0, `rgba(224,72,60,${0.45 * alpha})`);
        glow.addColorStop(1, "rgba(224,72,60,0)");
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 16, 0, Math.PI * 2);
        ctx.fill();

        ctx.beginPath();
        ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(224,72,60,${alpha})`;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(p.x, p.y, 8, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(224,72,60,${0.55 * alpha})`;
        ctx.lineWidth = 1.4;
        ctx.stroke();

        if (p.z > 0.45) {
          ctx.font = '600 10px ui-monospace, "SF Mono", Menlo, monospace';
          ctx.textAlign = "center";
          const label = o.region;
          const w = ctx.measureText(label).width;
          // a small plate keeps the caption legible over the dot field
          ctx.fillStyle = `rgba(255,255,255,${0.88 * alpha})`;
          ctx.fillRect(p.x - w / 2 - 4, p.y + o.labelDy - 8, w + 8, 13);
          ctx.fillStyle = `rgba(10,18,48,${alpha})`;
          ctx.fillText(label, p.x, p.y + o.labelDy + 2);
        }
      });

      if (!reduce) {
        spin += 0.18;
        frame = window.requestAnimationFrame(draw);
      }
    };

    const start = () => {
      window.cancelAnimationFrame(frame);
      resize();
      draw();
    };

    const onVisibility = () => {
      if (document.hidden) window.cancelAnimationFrame(frame);
      else if (!reduce) frame = window.requestAnimationFrame(draw);
    };

    start();
    window.addEventListener("resize", start);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", start);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <div className="globe">
      <canvas ref={canvasRef} aria-hidden="true" />
      <ul className="globe-legend">
        {OFFICES.map((o) => (
          <li key={o.region}>
            <span className="pin" aria-hidden="true" />
            <span className="mono">{o.region}</span>
          </li>
        ))}
      </ul>
      <p className="sr-only">
        New Age Services operates from the United States, Canada and India.
      </p>
    </div>
  );
}

export default GlobeOffices;
